<script>
import { defineComponent, onMounted, ref, computed, watch } from 'vue';
import { isDate } from 'class-validator';
import LimitsLegend from './LimitsLegend.vue';
import LimitProgressBar from './LimitProgressBar.vue';
import NoLimitsOverlay from './NoLimitsOverlay.vue';
import { DateTime } from 'luxon';
import { useNotify, useRouter } from '@/composables';
import { useI18n } from 'vue-i18n';
import unitLimitsService from '@satellite/services/unit-limits-service.js';
import dockService from '@satellite/services/dock-service';
import loadTypeService from '@satellite/services/loadtype-service';
import loadTypeGroupService from '@satellite/services/loadtype-group-service';
import { UnitType, UnitGrouper, LoadTypeDirection } from '@nova/core';

export default defineComponent({
  name: 'LimitsOverviewTab',
  components: {
    LimitsLegend,
    LimitProgressBar,
    NoLimitsOverlay
  },
  props: {
    warehouseId: {
      type: String,
      required: true
    },
    unitGrouperType: {
      type: String,
      required: true,
      validator: value =>
        [
          UnitGrouper.Warehouse,
          UnitGrouper.Dock,
          UnitGrouper.LoadType,
          UnitGrouper.LoadTypeGroup
        ].includes(value)
    },
    dateMode: {
      type: String,
      required: true,
      validator: value => ['day', 'month'].includes(value)
    },
    baseDate: {
      type: Date,
      required: true,
      validator: isDate
    },
    timezone: {
      type: String,
      default: 'UTC'
    }
  },
  emits: ['close-drawer'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const isLoading = ref(false);
    const limits = ref(null);
    const allLimitsFlat = ref([]); // Store flat array for getLimitValue
    const router = useRouter();
    const expandedPanels = ref([0]);
    const docks = ref([]);
    const notify = useNotify();
    const loadTypes = ref([]);
    const loadTypeGroups = ref([]);

    // Track progress percentages for each limit using a reactive object instead of Map
    const limitProgressPercentages = ref({});

    // Fetch limit configurations for the current warehouse and grouper type
    const getLimitsConfig = async () => {
      return await unitLimitsService.getConfiguration({
        warehouseId: props.warehouseId,
        unitGrouperType: props.unitGrouperType
      });
    };

    // Compute start and end dates based on date mode (day or month)
    const requestDates = computed(() => {
      const base = DateTime.fromJSDate(props.baseDate).setZone(props.timezone);
      const startDate = props.dateMode === 'day' ? base : base.startOf('month');
      const endDate = props.dateMode === 'day' ? base : base.endOf('month');

      return {
        countedDateStart: startDate.toISODate(),
        countedDateEnd: endDate.endOf('day').toISODate()
      };
    });

    // Fetch count data for all limits within the date range
    const getLimitsData = async allLimits => {
      if (!allLimits?.length) {
        return null;
      }
      return await unitLimitsService.getCount({
        unitLimitIds: allLimits?.map(l => l.id),
        countedDateStart: requestDates.value.countedDateStart,
        countedDateEnd: requestDates.value.countedDateEnd
      });
    };

    // Compute the field name for limits based on date mode (e.g., 'mondayLimit' or 'januaryLimit')
    const limitFieldName = computed(() => {
      const limitDate = DateTime.fromJSDate(props.baseDate);
      const period =
        props.dateMode === 'day'
          ? limitDate.toFormat('cccc').toLowerCase()
          : limitDate.monthLong.toLowerCase();

      return `${period}Limit`;
    });

    // Map of limit IDs to their values for efficient lookup
    const limitValuesMap = computed(() => {
      const map = new Map();
      allLimitsFlat.value?.forEach(limit => {
        const limitValue = limit[limitFieldName.value] ?? 0;
        map.set(limit.id, limitValue);
      });
      return map;
    });

    // Get the limit value for a specific limit ID
    const getLimitValue = limitId => {
      return limitValuesMap.value.get(limitId) ?? 0;
    };

    // Get the display title for a limit based on its grouper type
    const setLimitTitle = limit => {
      if (props.unitGrouperType === UnitGrouper.Dock && docks.value?.length > 0) {
        const dock = docks.value.find(d => d.id === limit.unitGrouperId);
        return dock ? dock.name : t('appointment.dialogs.limitsOverviewDialog.headers.dock');
      }

      if (props.unitGrouperType === UnitGrouper.Warehouse) {
        return t('appointment.dialogs.limitsOverviewDialog.headers.warehouse');
      }

      if (props.unitGrouperType === UnitGrouper.LoadType && loadTypes.value?.length > 0) {
        const loadType = loadTypes.value.find(lt => lt.id === limit.unitGrouperId);
        if (loadType) {
          return `${loadType.name} (${loadType.direction.toUpperCase()})`;
        }
        return t('appointment.dialogs.limitsOverviewDialog.headers.loadType');
      }

      if (props.unitGrouperType === UnitGrouper.LoadTypeGroup && loadTypeGroups.value?.length > 0) {
        const loadTypeGroup = loadTypeGroups.value.find(ltg => ltg.id === limit.unitGrouperId);
        return loadTypeGroup
          ? loadTypeGroup.name
          : t('appointment.dialogs.limitsOverviewDialog.headers.loadTypeGroup');
      }

      return limit.title || t('appointment.dialogs.limitsOverviewDialog.headers.limit');
    };

    // Aggregate count data by unitLimitId (sum counts across all dates)
    const aggregateLimitsCount = countData => {
      if (!countData || countData.length === 0) {
        return [];
      }

      // For all types: aggregate counts by unitLimitId (sum across all days/dates)
      const aggregatedByLimitId = {};

      countData.forEach(item => {
        if (!aggregatedByLimitId[item.unitLimitId]) {
          aggregatedByLimitId[item.unitLimitId] = 0;
        }
        aggregatedByLimitId[item.unitLimitId] += +item.unitCount || 0;
      });

      return Object.entries(aggregatedByLimitId).map(([unitLimitId, unitCount]) => ({
        unitCount,
        unitLimitId
      }));
    };

    // Expand all expansion panels to show all limit details
    const expandAllPanels = () => {
      expandedPanels.value = Object.keys(limits.value || {}).map((_, index) => index) ?? [];
    };

    // Fetch and process all limits data: configs, counts, and group by unitGrouperId
    const fetchData = async () => {
      isLoading.value = true;

      try {
        limitProgressPercentages.value = {};

        const [docksData, loadTypesData, loadTypeGroupsData, allLimits] = await Promise.all([
          dockService.getListDocks({ warehouseId: props.warehouseId }),
          loadTypeService.getLoadtypes({
            warehouseId: props.warehouseId,
            fields: ['id,name,direction,duration_min']
          }),
          loadTypeGroupService.getLoadTypeGroups(props.warehouseId),
          getLimitsConfig()
        ]);

        docks.value = docksData;
        loadTypes.value = loadTypesData;
        loadTypeGroups.value = loadTypeGroupsData || [];

        const loadTypeDirectionMap = new Map(loadTypes.value.map(lt => [lt.id, lt.direction]));
        const filteredLimits =
          allLimits?.filter(limit => {
            if (
              limit.unitGrouperType === UnitGrouper.Warehouse ||
              limit.unitGrouperType === UnitGrouper.Dock
            ) {
              return true;
            }
            if (limit.unitGrouperType === UnitGrouper.LoadType) {
              const loadTypeDirection = loadTypeDirectionMap.get(limit.unitGrouperId);
              // For Units: only show INBOUND load types
              // For Appointments: show both INBOUND and OUTBOUND
              if (limit.unitType === UnitType.Units) {
                return loadTypeDirection === LoadTypeDirection.Inbound;
              }
              return true;
            }
            return true;
          }) || [];

        const countData = await getLimitsData(filteredLimits);
        const aggregatedCountData = aggregateLimitsCount(countData);
        const countMap = new Map(aggregatedCountData?.map(c => [c.unitLimitId, c.unitCount ?? 0]));
        const limitsWithValues = filteredLimits
          ?.map(limit => ({
            ...limit,
            unitCount: countMap.get(limit.id) ?? 0,
            title: setLimitTitle(limit)
          }))
          .filter(limit => {
            const limitValue = limit[limitFieldName.value] ?? 0;
            return limitValue > 0;
          });

        allLimitsFlat.value = limitsWithValues || [];

        const groupedLimits = limitsWithValues?.reduce((acc, limit) => {
          const grouperId = limit.unitGrouperId || 'warehouse';
          if (!acc[grouperId]) {
            acc[grouperId] = [];
          }
          acc[grouperId].push(limit);
          return acc;
        }, {});

        limits.value = groupedLimits;
        expandAllPanels();
      } catch (error) {
        console.error('Error fetching limits data:', error);
        notify(t('appointment.dialogs.limitsOverviewDialog.messages.fetchError'), 'error');
      } finally {
        isLoading.value = false;
      }
    };

    // Navigate to limit settings page
    const goToLimitSettings = () => {
      router.push(
        `/warehouses/${props.warehouseId}/settings?active=limits&limitType=${UnitType.Appointment}`
      );
      emit('close-drawer');
    };

    // Handle progress bar percentage changes and expand panels if capacity reached
    const handleProgressChange = (limitId, { progressPercentage }) => {
      limitProgressPercentages.value[limitId] = progressPercentage;

      if (progressPercentage >= 100) {
        expandAllPanels();
      }
    };

    // Check if any limit in a group has reached 100% capacity
    const isCapacityReached = grouperId => {
      const group = limits.value?.[grouperId];
      if (!group || !Array.isArray(group)) return false;

      return group.some(limit => (limitProgressPercentages.value[limit.id] ?? 0) >= 100);
    };

    // Get translated label for unit type (Units or Appointments)
    const getUnitTypeLabel = unitType => {
      return unitType === UnitType.Units
        ? t('appointment.dialogs.limitsOverviewDialog.labels.units')
        : t('appointment.dialogs.limitsOverviewDialog.labels.appointments');
    };

    onMounted(async () => {
      await fetchData();
    });

    watch(
      () => [props.baseDate, props.dateMode, props.unitGrouperType],
      async () => {
        await fetchData();
      }
    );

    return {
      t,
      limits,
      allLimitsFlat,
      isLoading,
      getLimitValue,
      goToLimitSettings,
      expandedPanels,
      handleProgressChange,
      isCapacityReached,
      getUnitTypeLabel
    };
  }
});
</script>
<template>
  <div class="d-flex flex-column gap-4">
    <limits-legend type="counter" />

    <div class="d-flex justify-center align-center h-100 px-3" v-if="isLoading">
      <v-progress-linear indeterminate color="primary"></v-progress-linear>
    </div>
    <v-expansion-panels
      elevation="0"
      readonly
      hide-actions
      multiple
      v-model="expandedPanels"
      v-else-if="limits && Object.keys(limits).length > 0">
      <v-expansion-panel
        class="border border-radius d-flex flex-column gap-4"
        v-for="(limitGroup, grouperId) in limits"
        :key="grouperId"
        :data-testid="`unit-limiter-expansion-panel-${grouperId}`">
        <v-expansion-panel-title class="bg-disabled">
          <div class="d-flex flex-row align-center w-100">
            <span>
              {{ limitGroup[0]?.title }}
            </span>
            <v-spacer />
            <v-chip
              v-if="isCapacityReached(grouperId)"
              size="small"
              variant="tonal"
              color="error"
              class="font-weight-bold">
              <v-icon icon="mdi-alert-circle-outline" size="small" class="mr-1"></v-icon>
              {{ t('appointment.dialogs.limitsOverviewDialog.messages.capacityReached') }}
            </v-chip>
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text eager>
          <div class="d-flex flex-column gap-4">
            <div
              v-for="limit in limitGroup"
              :key="limit.id"
              :data-testid="`unit-limiter-limit-progress-${limit.id}`">
              <limit-progress-bar
                :test-id="`unit-limiter-progress-bar-${grouperId}-${limit.unitType}`"
                :title="getUnitTypeLabel(limit.unitType)"
                :limit-value="getLimitValue(limit.id)"
                :count="limit.unitCount ?? 0"
                :date-mode="dateMode"
                :base-date="baseDate"
                @progress-percentage-changed="
                  eventData => handleProgressChange(limit.id, eventData)
                " />
            </div>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
    <div
      v-else
      class="my-4 d-flex flex-column align-center gap-4"
      :data-testid="`unit-limiter-no-limits-overlay-${unitGrouperType}`">
      <no-limits-overlay :entity="unitGrouperType" />
      <primary-button @click="goToLimitSettings" data-testid="unit-limiter-add-limits-button">
        {{ t('appointment.dialogs.limitsOverviewDialog.labels.addLimits') }}
      </primary-button>
    </div>
  </div>
</template>
